import { CurrentUserModel } from "../models/ProfileModel";
import { UtilityHelper } from "../utils/UtilityHelper";
import { tokenService } from "./tokenService";

/** Handles all responsibilities related to authentication */
export const authService = {
  /**
   * Get current user from localStorage (JWT-based only)
   */
  getCurrentUser: (): CurrentUserModel => {
    const userInfoJson = localStorage.getItem("userInfo");
    if (userInfoJson) {
      try {
        return JSON.parse(userInfoJson);
      } catch (error) {
        console.error("Failed to parse user info from localStorage:", error);
        localStorage.removeItem("userInfo");
      }
    }

    return CurrentUserModel.init();
  },

  /**
   * Store user info in localStorage
   */
  setCurrentUser: (user: CurrentUserModel): void => {
    localStorage.setItem("userInfo", JSON.stringify(user));
  },

  /**
   * Force logout - clears tokens and redirects
   */
  logout: async () => {
    sessionStorage.clear();

    // Tracking log out event in analytics
    UtilityHelper.onEventAnalytics("Logout", "Logout", "Logout");

    // Revoke refresh token on backend
    const refreshToken = tokenService.getRefreshToken();
    if (refreshToken) {
      await tokenService.logout(refreshToken);
    }

    // Clear JWT tokens
    tokenService.clearTokens();
    localStorage.removeItem("userInfo");

    // Redirect to login (will be handled by MSAL in new flow)
    window.location.href = `/`;
  },

  /**
   * Redirects user to authenticate themselves
   * @param scheme : scheme name (deprecated - all users now use Azure AD)
   * @deprecated All users (WHO and non-WHO) now use Azure AD authentication with MSAL + JWT
   * Non-WHO users must be invited as guest users (B2B) to the Azure AD tenant
   */
  challenge: (scheme: string = "") => {
    // All authentication is now handled by MSAL (Azure AD)
    // Redirect to home page where MSAL will handle login
    console.warn(
      "authService.challenge() is deprecated. All users now use MSAL login."
    );
    window.location.href = "/";
  },

  /**
   * Check if user is authenticated (JWT-based only)
   */
  isAuthenticated: () => {
    return tokenService.hasValidTokens();
  },

  /**
   * Get tracking id for analytics
   */
  getTrackingId: () => {
    const currentUser = authService.getCurrentUser();
    return currentUser.trackingId;
  },

  /**
   * Check before rendering private routes
   */
  canRenderPrivateRoute: () => {
    const currentUser = authService.getCurrentUser();
    const isAuthenticated = authService.isAuthenticated();

    return isAuthenticated || (!isAuthenticated && currentUser.isNewUser);
  },

  /**
   * Check if current user is deactivated
   */
  isUserDeactivated: () => {
    const currentUser = authService.getCurrentUser();
    return currentUser.isDeactivated === true;
  },

  /**
   * Check if current user is inactive
   */
  isUserInactive: () => {
    const currentUser = authService.getCurrentUser();
    return currentUser.isActive === false;
  },
};
